import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import {
  Link,
  ReminderDefScheduleDto,
  ReminderDefSummaryDto,
  ReminderDefService,
} from '@savvy/reminders';
import { AppointmentQuickLinksEditorComponent } from '../editor/appointment-quick-links-editor/appointment-quick-links-editor.component';
import { environment } from '../../../environments/environment';
import { ContextService } from '../context.service';
import { resolveReminderWhatsAppTemplateKey } from '../smsSettings/whatsapp/whatsapp-reminder-template';
import { WhatsAppConnectionService } from '../smsSettings/whatsapp/whatsapp-connection.service';
import { WhatsAppChannelAccessService } from '../smsSettings/whatsapp/whatsapp-channel-access.service';
import { FloSnackbarComponent } from '../snackbar/floSnackbar.component';
import {
  fromReminderDefV3Response,
  ReminderDefV3Schedule,
  ReminderDefV3Summary,
  toReminderDefV3Payload,
} from './reminderDefV3.adapter';

type ReminderChannel = 'email' | 'sms';

interface ReminderScheduleOption {
  label: string;
  schedule: ReminderDefV3Schedule;
}

@Component({
  selector: 'app-add-edit-reminder-def',
  templateUrl: './addEditReminderDef.component.html',
  styleUrls: ['./addEditReminderDef.component.scss'],
})
export class AddEditReminderDefComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @ViewChild('emailQuicklinksMessageEditor', { static: false }) emailQuicklinksMessageEditor: AppointmentQuickLinksEditorComponent;
  @ViewChild('quickLinksEditorV2Component', { static: false }) quickLinksEditorV2Component: AppointmentQuickLinksEditorComponent;

  @Input() reminderDef: ReminderDefV3Summary;

  @Output() reminderUpdated = new EventEmitter<ReminderDefV3Summary>();
  @Output() reminderCancelled = new EventEmitter<void>();

  readonly channels: ReminderChannel[] = ['email', 'sms'];

  readonly isWhatsAppConfigured = !!(environment.metaAppId && environment.metaWhatsAppConfigId);

  /** v3 rules: WhatsApp is offered only when connected; its toggle is locked while WhatsApp sending is off. */
  isWhatsAppConnected = false;
  isWhatsAppSendingEnabled = false;

  activeIndex = 0;
  private connectionSub: Subscription;
  private scheduleOptionsCache: ReminderScheduleOption[] = [];
  private scheduleOptionsCacheType: string | null = null;

  constructor(
    private reminderApi: ReminderDefService,
    private translateService: TranslateService,
    private notify: FloSnackbarComponent,
    private whatsAppApi?: WhatsAppConnectionService,
    private contextService?: ContextService,
    private whatsAppAccess?: WhatsAppChannelAccessService) {
    console.info('[v2 reminders] add-edit reminder definition component constructed');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reminderDef']) {
      this.logLifecycle('reminder definition input received');
    }
  }

  ngOnInit(): void {
    this.logLifecycle('add-edit reminder definition component initialized');
    this.loadWhatsAppConnection();
  }

  ngOnDestroy(): void {
    this.connectionSub?.unsubscribe();
  }

  private loadWhatsAppConnection(): void {
    if (!this.isWhatsAppConfigured || !this.whatsAppApi || !this.contextService || !this.whatsAppAccess) {
      return;
    }
    this.connectionSub = this.contextService.contextIdDto$.pipe(
      filter(contextIdDto => !!contextIdDto?.contextId),
      switchMap(contextIdDto => this.whatsAppAccess.isAllowed(contextIdDto.contextId, contextIdDto.contextIdType).pipe(
        switchMap(allowed => allowed
          ? this.whatsAppApi.getConnection(contextIdDto.contextId, contextIdDto.contextIdType)
          : of(null)),
        // Not on the plan, or the connection failed: treat WhatsApp as not connected.
        catchError(() => of(null)))))
      .subscribe(connection => {
        this.isWhatsAppConnected = !!connection?.isConnected;
        this.isWhatsAppSendingEnabled = !!connection?.isSendingEnabled;
      });
  }

  ngAfterViewInit(): void {
    this.logLifecycle('add-edit reminder definition view initialized');
  }

  hasWhatsAppTab(): boolean {
    return this.isWhatsAppConfigured
      && this.isWhatsAppConnected
      && resolveReminderWhatsAppTemplateKey(this.reminderDef?.type) !== null;
  }

  /** v3 isWhatsAppDeliveryAvailable: connected and WhatsApp sending turned on. */
  isWhatsAppDeliveryAvailable(): boolean {
    return this.isWhatsAppConnected && this.isWhatsAppSendingEnabled;
  }

  /** v3 isReminderSmsLocked: SMS is the backup and cannot be turned off while WhatsApp is selected. */
  isSmsLocked(): boolean {
    return this.isWhatsAppDeliveryAvailable() && !!this.reminderDef?.whatsAppConfig?.enabled;
  }

  selectedTabChange(event: { index: number }) {
    this.activeIndex = event.index;
  }

  cancel() {
    this.reminderCancelled.emit();
  }

  update() {
    this.prepareReminder();
    if (!this.canSave()) {
      return;
    }

    this.reminderApi.updateReminderDefV3(toReminderDefV3Payload(this.reminderDef) as ReminderDefSummaryDto).subscribe(reminderDef => {
      this.reminderDef = fromReminderDefV3Response(reminderDef);
      this.reminderUpdated.emit(this.reminderDef);
      this.translateService.get('Updated Reminder').subscribe(val => {
        this.notify.message = val;
        this.notify.open();
      });
    });
  }

  getSchedules(channel: ReminderChannel): Array<ReminderDefV3Schedule> {
    return channel === 'email'
      ? this.reminderDef?.emailConfig?.schedules ?? []
      : this.reminderDef?.smsConfig?.schedules ?? [];
  }

  isChannelEnabled(channel: ReminderChannel): boolean {
    return channel === 'email'
      ? this.reminderDef?.emailConfig?.enabled ?? false
      : this.reminderDef?.smsConfig?.enabled ?? false;
  }

  setWhatsAppEnabled(enabled: boolean) {
    if (!this.isWhatsAppSendingEnabled) {
      return;
    }
    this.reminderDef.whatsAppConfig = { ...this.reminderDef.whatsAppConfig, enabled };
    if (enabled && this.reminderDef.smsConfig && !this.reminderDef.smsConfig.enabled) {
      // v3 resolveSmsAfterWhatsAppChange: SMS must be on as the backup.
      this.reminderDef.smsConfig = { ...this.reminderDef.smsConfig, enabled: true };
    }
  }

  setChannelEnabled(channel: ReminderChannel, enabled: boolean) {
    if (channel === 'sms' && !enabled) {
      if (this.isSmsLocked()) {
        return;
      }
      if (this.reminderDef.whatsAppConfig?.enabled) {
        this.reminderDef.whatsAppConfig = { ...this.reminderDef.whatsAppConfig, enabled: false };
      }
    }
    if (channel === 'email') {
      this.reminderDef.emailConfig = { ...this.reminderDef.emailConfig, enabled };
      return;
    }
    this.reminderDef.smsConfig = { ...this.reminderDef.smsConfig, enabled };
  }

  scheduleOptions(): ReminderScheduleOption[] {
    const reminderType = this.reminderDef?.type ?? null;
    if (this.scheduleOptionsCacheType === reminderType) {
      return this.scheduleOptionsCache;
    }

    this.scheduleOptionsCacheType = reminderType;
    if (reminderType === 'CUSTOMERS_LAST_INVOICE') {
      this.scheduleOptionsCache = [1, 2, 3, 5, 7, 10, 14, 30, 60, 90, 120, 150, 180].map(days => this.scheduleOption(
        `${this.formatDuration(days)} after appointment`,
        ReminderDefScheduleDto.ScheduleTypeEnum.DaysAfter,
        days,
      ));
      return this.scheduleOptionsCache;
    }

    if (reminderType === 'PET_BIRTHDAY') {
      this.scheduleOptionsCache = [
        this.scheduleOption("72 hours before pet's birthday", ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 3),
        this.scheduleOption("48 hours before pet's birthday", ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 2),
        this.scheduleOption("24 hours before pet's birthday", ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 1),
        this.scheduleOption("on the day of pet's birthday", ReminderDefScheduleDto.ScheduleTypeEnum.OnSameDayOfYear, 0),
        this.scheduleOption("24 hours after pet's birthday", ReminderDefScheduleDto.ScheduleTypeEnum.DaysAfter, 1),
        this.scheduleOption("48 hours after pet's birthday", ReminderDefScheduleDto.ScheduleTypeEnum.DaysAfter, 2),
        this.scheduleOption("72 hours after pet's birthday", ReminderDefScheduleDto.ScheduleTypeEnum.DaysAfter, 3),
      ];
      return this.scheduleOptionsCache;
    }

    if (!['ENTITY_INSTANCE', 'APPOINTMENT', 'PET_BOARDING', 'VACCINATION', 'DEPOSIT'].includes(reminderType || '')) {
      this.scheduleOptionsCache = [];
      return this.scheduleOptionsCache;
    }

    const appointmentOptions = [
      this.scheduleOption('10 days before appointment', ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 10),
      this.scheduleOption('7 days before appointment', ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 7),
      this.scheduleOption('5 days before appointment', ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 5),
      this.scheduleOption('72 hours before appointment', ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 3),
      this.scheduleOption('48 hours before appointment', ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 2),
      this.scheduleOption('24 hours before appointment', ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore, 1),
      this.scheduleOption('on the day of appointment', ReminderDefScheduleDto.ScheduleTypeEnum.OnSameDayOfYear, 0),
    ];

    this.scheduleOptionsCache = reminderType === 'DEPOSIT'
      ? [this.scheduleOption('Immediately when appointment is created', ReminderDefScheduleDto.ScheduleTypeEnum.Immediate), ...appointmentOptions]
      : appointmentOptions;
    return this.scheduleOptionsCache;
  }

  trackByChannel(_index: number, channel: ReminderChannel): ReminderChannel {
    return channel;
  }

  trackByScheduleOption(_index: number, option: ReminderScheduleOption): string {
    return `${option.schedule.scheduleType}:${option.schedule.daysSchedule}`;
  }

  trackBySchedule(_index: number, schedule: ReminderDefV3Schedule): string {
    return `${schedule.scheduleType}:${schedule.daysSchedule}`;
  }

  scheduleOptionsFor(schedule: ReminderDefV3Schedule): ReminderScheduleOption[] {
    const options = this.scheduleOptions();
    return options.some(option => this.schedulesMatch(option.schedule, schedule))
      ? options
      : [...options, this.scheduleOption(
        this.fallbackScheduleLabel(schedule),
        schedule.scheduleType,
        schedule.daysSchedule,
      )];
  }

  canAddSchedule(channel: ReminderChannel): boolean {
    return this.scheduleOptions().some(option => !this.isScheduleSelected(channel, option.schedule));
  }

  addSchedule(channel: ReminderChannel): void {
    const schedule = this.scheduleOptions().find(option => !this.isScheduleSelected(channel, option.schedule));
    if (schedule) {
      this.setScheduleSelected(channel, schedule.schedule, true);
    }
  }

  updateSchedule(channel: ReminderChannel, current: ReminderDefV3Schedule, selected: ReminderDefV3Schedule): void {
    const updated = this.cleanSchedule({
      ...selected,
      timeToSend: this.isImmediate(selected) ? null : current.timeToSend,
    });
    this.setSchedules(channel, this.getSchedules(channel).map(schedule =>
      this.schedulesMatch(schedule, current) ? updated : schedule,
    ));
  }

  isScheduleSelected(channel: ReminderChannel, schedule: ReminderDefV3Schedule): boolean {
    return this.getSchedules(channel).some(item => this.schedulesMatch(item, schedule));
  }

  setScheduleSelected(channel: ReminderChannel, schedule: ReminderDefV3Schedule, selected: boolean) {
    const schedules = this.getSchedules(channel);
    this.setSchedules(channel, selected
      ? [...schedules.filter(item => !this.schedulesMatch(item, schedule)), this.cleanSchedule(schedule)]
      : schedules.filter(item => !this.schedulesMatch(item, schedule)));
  }

  isChannelMissingSchedule(channel: ReminderChannel): boolean {
    const config = channel === 'email' ? this.reminderDef?.emailConfig : this.reminderDef?.smsConfig;
    return Boolean(config?.enabled && !this.getSchedules(channel).length);
  }

  isScheduleTimeMissing(schedule: ReminderDefV3Schedule): boolean {
    return !this.isImmediate(schedule) && !this.isValidTimeToSend(schedule.timeToSend);
  }

  hasMissingScheduleTime(channel: ReminderChannel): boolean {
    return this.getSchedules(channel).some(schedule => this.isScheduleTimeMissing(schedule));
  }

  scheduleTimeValue(schedule: ReminderDefV3Schedule): string | null {
    return this.isValidTimeToSend(schedule.timeToSend) ? schedule.timeToSend.slice(0, 5) : null;
  }

  setScheduleTime(channel: ReminderChannel, schedule: ReminderDefV3Schedule, time: string) {
    this.setSchedules(channel, this.getSchedules(channel).map(item => this.schedulesMatch(item, schedule)
      ? { ...item, timeToSend: time.length === 5 ? `${time}:00` : time }
      : item));
  }

  scheduleTimeAsDate(schedule: ReminderDefV3Schedule): Date | null {
    if (!this.isValidTimeToSend(schedule.timeToSend)) {
      return null;
    }
    const date = new Date();
    const [hour, minute, second] = schedule.timeToSend.split(':').map(Number);
    date.setHours(hour, minute, second, 0);
    return date;
  }

  setScheduleTimeFromDate(channel: ReminderChannel, schedule: ReminderDefV3Schedule, time: Date): void {
    if (!time) {
      return;
    }
    const pad = (value: number) => value.toString().padStart(2, '0');
    this.setScheduleTime(
      channel,
      schedule,
      `${pad(time.getHours())}:${pad(time.getMinutes())}:${pad(time.getSeconds())}`,
    );
  }

  removeSchedule(channel: ReminderChannel, schedule: ReminderDefV3Schedule) {
    this.setSchedules(channel, this.getSchedules(channel).filter(item => !this.schedulesMatch(item, schedule)));
  }

  formatSchedule(schedule: ReminderDefV3Schedule): string {
    return this.scheduleOptions().find(option => this.schedulesMatch(option.schedule, schedule))?.label
      || this.fallbackScheduleLabel(schedule);
  }

  canSave(): boolean {
    return !this.isChannelMissingSchedule('email')
      && !this.isChannelMissingSchedule('sms')
      && !this.hasMissingScheduleTime('email')
      && !this.hasMissingScheduleTime('sms');
  }

  private prepareReminder() {
    if (this.activeIndex === 0 && this.reminderDef.emailConfig && this.emailQuicklinksMessageEditor) {
      this.reminderDef.emailConfig.body = this.emailQuicklinksMessageEditor.getEditorText();
      this.reminderDef.emailConfig.links = this.emailQuicklinksMessageEditor.getLinksFromQuicklinksData() as Link[];
    }
    if (this.activeIndex === 1 && this.reminderDef.smsConfig && this.quickLinksEditorV2Component) {
      this.reminderDef.smsConfig.text = this.quickLinksEditorV2Component.getEditorText();
      this.reminderDef.smsConfig.links = this.quickLinksEditorV2Component.getLinksFromQuicklinksData() as Link[];
    }
  }

  private setSchedules(channel: ReminderChannel, schedules: Array<ReminderDefV3Schedule>) {
    if (channel === 'email') {
      this.reminderDef.emailConfig = {
        enabled: false,
        ...this.reminderDef.emailConfig,
        schedules,
      };
      return;
    }
    this.reminderDef.smsConfig = {
      enabled: false,
      ...this.reminderDef.smsConfig,
      schedules,
    };
  }

  private scheduleOption(
    label: string,
    scheduleType: ReminderDefScheduleDto.ScheduleTypeEnum,
    daysSchedule?: number | null,
  ): ReminderScheduleOption {
    return { label, schedule: this.cleanSchedule({ scheduleType, daysSchedule }) };
  }

  private cleanSchedule(schedule: ReminderDefV3Schedule): ReminderDefV3Schedule {
    return this.isImmediate(schedule)
      ? { scheduleType: schedule.scheduleType, daysSchedule: null, timeToSend: null }
      : {
        scheduleType: schedule.scheduleType,
        daysSchedule: schedule.daysSchedule,
        timeToSend: schedule.timeToSend ?? null,
      };
  }

  schedulesMatch(first: ReminderDefV3Schedule, second: ReminderDefV3Schedule): boolean {
    return first.scheduleType === second.scheduleType && first.daysSchedule === second.daysSchedule;
  }

  private isImmediate(schedule: ReminderDefV3Schedule): boolean {
    return schedule.scheduleType === ReminderDefScheduleDto.ScheduleTypeEnum.Immediate;
  }

  private isValidTimeToSend(timeToSend: string | null | undefined): timeToSend is string {
    return typeof timeToSend === 'string' && /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(timeToSend);
  }

  private fallbackScheduleLabel(schedule: ReminderDefV3Schedule): string {
    if (this.isImmediate(schedule)) {
      return 'Immediately when appointment is created';
    }
    const subject = this.reminderDef?.type === 'PET_BIRTHDAY' ? "pet's birthday" : 'appointment';
    if (schedule.scheduleType === ReminderDefScheduleDto.ScheduleTypeEnum.OnSameDayOfYear) {
      return `On the day of ${subject}`;
    }
    return `${this.formatDuration(schedule.daysSchedule || 0)} ${schedule.scheduleType === ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore ? 'before' : 'after'} ${subject}`;
  }

  private formatDuration(days: number): string {
    const months = days / 30;
    return Number.isInteger(months) && months > 0
      ? `${months} month${months === 1 ? '' : 's'}`
      : `${days} day${days === 1 ? '' : 's'}`;
  }

  private logLifecycle(event: string): void {
    console.info(`[v2 reminders] ${event}`, {
      hasReminderDefinition: Boolean(this.reminderDef),
      reminderType: this.reminderDef?.type ?? null,
      emailScheduleCount: this.reminderDef?.emailConfig?.schedules?.length ?? 0,
      smsScheduleCount: this.reminderDef?.smsConfig?.schedules?.length ?? 0,
    });
  }
}
